import React, { use, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Image from "next/image";
import moment from "moment";
import { addDays } from "date-fns";
import SweetAlert from "sweetalert2";
import { API } from "@/app/api/config";
import { customAxios } from "@/modules/common/api";

interface StayInfo {
  imageUrl?: string;
  storeName?: string;
  discountPrice?: number;
  salePrice?: number;
  region?: string;
  city?: string;
  standardPeopleCount?: number;
  maximumPersonnelCount?: number;
}

interface Props {
  id?: number;
  type?: string;
  stayInfo?: StayInfo;
  isDesktop?: boolean;
  fillWidth?: boolean;
  checkAvailability?: boolean; // true면 선택한 날짜에 예약 가능한 객실이 있는지 확인한 뒤 이동
}

const StayItemBox = ({ id, type, stayInfo, isDesktop, fillWidth, checkAvailability }: Props) => {
  const router = useRouter();

  const startDate = moment(new Date()).format("YYYY-MM-DD");
  const endDate = moment(addDays(new Date(), 1)).format("YYYY-MM-DD");

  const [isDiscount, setIsDisCount] = useState(false); //할인 여부
  const isCheckingRef = useRef(false); // 예약 가능 여부 확인 중 중복 클릭 방지

  const handleMovePage = async () => {
    if (checkAvailability) {
      if (isCheckingRef.current) return;
      isCheckingRef.current = true;

      try {
        // 헤더에서 선택한 날짜(없으면 오늘~내일) 기준으로 예약 가능한 객실이 하나라도 있는지 확인
        const params = new URLSearchParams(window.location.search);
        const period = JSON.parse(params.get("header-period") || "null");
        const headcount = JSON.parse(params.get("header-headcount") || "null");
        const start = period?.start ?? startDate;
        const end = period?.end ?? endDate;
        const adult = headcount?.adult ?? 1;
        const kid = headcount?.kid ?? 0;

        const res = await customAxios.get(`${API.ROOM_PRICE}`, {
          params: {
            partnerStoreId: id,
            startDate: start,
            endDate: end,
            adultCount: adult,
            kidCount: kid,
            petCount: headcount?.pet ?? 0,
            diffDays: Math.ceil(
              Math.abs(new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24)
            ),
          },
        });
        const roomList = res?.data?.response?.data ?? [];
        // 숙소 상세 페이지와 같은 기준: 예약 가능하면서 선택한 인원이 객실 최대 인원 이내여야 한다
        const isAvailable = roomList.some(
          (room: { isReservationAvailability?: boolean; roomTypeVo?: { maximumPersonnelCount?: number } }) =>
            room.isReservationAvailability &&
            adult + kid <= (room.roomTypeVo?.maximumPersonnelCount ?? Infinity)
        );
        if (!isAvailable) {
          SweetAlert.fire({
            text: "예약 가능한 객실이 없습니다.",
            icon: "warning",
            showConfirmButton: true,
          });
          return;
        }
      } catch {
        // 확인에 실패해도(깨진 주소 값 포함) 숙소 페이지로는 이동할 수 있게 한다
      } finally {
        isCheckingRef.current = false;
      }
    }

    // 헤더에서 날짜·인원을 골랐다면 숙소 페이지로도 그대로 넘긴다(안 넘기면 숙소 페이지가 오늘~내일로 바꿔 버린다).
    // 고르지 않았다면 기존처럼 주소에 아무것도 붙이지 않는다.
    const current = new URLSearchParams(window.location.search);
    const next = new URLSearchParams();
    ["header-period", "header-headcount"].forEach((key) => {
      const value = current.get(key);
      if (value) next.set(key, value);
    });
    const query = next.toString();
    router.push("/partner/store/" + id + (query ? "?" + query : ""));

    // router.push(
    //   "/partner/store/" +
    //     id +
    //     "?header-period=" +
    //     JSON.stringify({
    //       start: moment(startDate).format("YYYY-MM-DD"),
    //       end: moment(endDate).format("YYYY-MM-DD"),
    //     }) +
    //     "&header-headcount=" +
    //     JSON.stringify({
    //       adult: 1,
    //       child: 0,
    //       pet: 0,
    //     })
    // );
  };

  return (
    <div className="flex justify-center">
      <div
        className={fillWidth ? "flex flex-col w-full cursor-pointer" : "inline-flex flex-col cursor-pointer"}
        onClick={handleMovePage}
      >
        {fillWidth && (
          <Image
            className="w-full h-auto aspect-[350/230] mb-[24px] object-cover"
            src={stayInfo?.imageUrl || '/images/stay-default-image.svg'}
            alt={stayInfo?.storeName || '숙소 이미지'}
            width={350}
            height={230}
            sizes="(max-width: 768px) 100vw, (max-width: 1360px) 50vw, 350px"
          />
        )}
        {!fillWidth && isDesktop && (
          <>
            {type === "A" ? (
              <Image
                className="w-[520px] h-[347px] mb-[24px] object-cover"
                src={stayInfo?.imageUrl || '/images/stay-default-image.svg'}
                alt={stayInfo?.storeName || '숙소 이미지'}
                width={520}
                height={347}
                sizes="520px"
              />
            ) : (
              <Image
                className="w-[420px] h-[300px] mb-[24px] object-cover"
                src={stayInfo?.imageUrl || '/images/stay-default-image.svg'}
                alt={stayInfo?.storeName || '숙소 이미지'}
                width={420}
                height={300}
                sizes="420px"
              />
            )}
          </>
        )}
        {!fillWidth && !isDesktop && (
          <Image
            className="w-[350px] h-[230px] mb-[24px] object-cover"
            src={stayInfo?.imageUrl || '/images/stay-default-image.svg'}
            alt={stayInfo?.storeName || '숙소 이미지'}
            width={350}
            height={230}
            sizes="350px"
          />
        )}
        <div className="flex justify-between items-center gap-[8px] mb-[1px]">
          <div className="flex-1 min-w-0 truncate text-[20px] font-[500] mobile:text-[16px]">
            {stayInfo?.storeName}
          </div>
          <div className="flex-shrink-0">
            <div
              className={` ${
                stayInfo?.discountPrice
                  ? "text-[14px] text-[#929292] line-through font-[400]"
                  : "text-[16px] font-[500]"
              }`}
            >
              ₩ {stayInfo?.salePrice?.toLocaleString()}
            </div>
            {!!stayInfo?.discountPrice && (
              <div className="mt-[-5px]">
                ₩ {stayInfo?.discountPrice?.toLocaleString()}
              </div>
            )}
          </div>
        </div>
        <div className="text-[14px] font-[400]">
          {stayInfo?.region} | {stayInfo?.city} | 기준 {stayInfo?.standardPeopleCount}인 (최대
          {stayInfo?.maximumPersonnelCount}인)
          {/* 강원 / 강릉시 | 기준 2인(최대 4인) */}
        </div>
      </div>
    </div>
  );
};

export default StayItemBox;
